import React from 'react'
import { IconPlus, IconX } from '@tabler/icons-react'
import { isEmpty } from 'lodash'

import classNames from 'classnames'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'

import {
  describeLink,
  describeLinkFully,
  findPromptField,
  getConditionChipVariant,
} from '../helpers/fieldLinks.helpers'
import { toFieldReference } from '../helpers/fieldTokens.helpers'

// One line under a field's name listing the conditions that gate it. Only rows
// that actually have a condition render a strip, so an unlinked field costs no
// extra height — the first condition is added from the row's branch button, and
// the strip carries the shortcut for the ones after it.
//
// Several conditions on one field all have to pass — that is fan-in on the
// backend — so they read as sibling chips rather than a nested expression.
export const FieldLinkStrip = ({
  promptFields,
  links,
  tokenSources = [],
  optionNames,
  onEdit,
  onRemove,
  onAdd,
  canAdd,
  readOnly,
  index,
  // A logic field's conditions are the terms it combines, not gates on whether
  // it runs: its chips drop the "Only when" and are joined by the combinator's
  // word instead.
  chipLabel = 'Only when',
  joiner,
}) => {
  if (isEmpty(links) && isEmpty(tokenSources)) {
    return null
  }

  return (
    <div className="rtn-link-strip" data-testid={`field-link-strip-${index}`}>
      {links.map((link, linkIndex) => {
        const sourceField = findPromptField(promptFields, link.source)
        const fullText = describeLinkFully(link, sourceField, optionNames)
        const { pillProps, isPickList } = getConditionChipVariant(sourceField)
        return (
          <React.Fragment
            // A gate's identity: both ways links enter the form dedupe on exactly
            // these four, so no two chips on a field can share a key.
            key={`${link.source}-${link.target}-${link.operator}-${link.compare_value}`}
          >
            {joiner && linkIndex > 0 && (
              <span className="rtn-link-strip__joiner" data-testid={`field-link-joiner-${index}`}>
                {joiner}
              </span>
            )}
            <span className="rtn-link-chip">
              <Pill
                small
                clickable={!readOnly}
                outlined
                className={classNames({ 'rtn-link-chip--picklist': isPickList })}
                {...pillProps}
                tooltip={readOnly ? fullText : `${fullText} - click to edit`}
                onClick={readOnly ? undefined : () => onEdit(link)}
                dataTestId={`field-link-chip-${index}`}
                // Pill renders a div, so an editable chip needs button semantics
                // of its own to stay keyboard-reachable.
                {...(readOnly
                  ? {}
                  : {
                      role: 'button',
                      tabIndex: 0,
                      onKeyDown: (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          onEdit(link)
                        }
                      },
                    })}
              >
                {chipLabel && <span className="rtn-link-chip__label">{chipLabel}</span>}
                <span className="rtn-link-chip__text">
                  {describeLink(link, sourceField, optionNames)}
                </span>
              </Pill>
              {!readOnly && (
                <Button
                  basic
                  small
                  square
                  type="button"
                  tooltip="Remove this condition"
                  onClick={() => onRemove(link)}
                  data-testid={`field-link-remove-${index}`}
                >
                  <IconX />
                </Button>
              )}
            </span>
          </React.Fragment>
        )
      })}

      {tokenSources.map((source) => (
        <Pill
          small
          outlined
          className="rtn-link-chip rtn-link-chip--uses"
          tooltip={`This field's prompt receives ${source.key}'s answer`}
          dataTestId={`field-uses-chip-${index}`}
          key={source.key}
        >
          <span className="rtn-link-chip__label">Uses</span>
          <span className="rtn-link-chip__token">{toFieldReference(source.key)}</span>
        </Pill>
      ))}

      {!readOnly && canAdd && (
        <Button basic small type="button" onClick={onAdd} data-testid={`field-link-add-${index}`}>
          <IconPlus />
          Add another
        </Button>
      )}
    </div>
  )
}
